import {useIntl, FormattedNumber} from 'react-intl'
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'

import {
    useMultiStyleConfig,
    Box,
    Flex,
    IconButton,
    Badge,
    Popover,
    PopoverHeader,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    PopoverFooter,
    PopoverArrow,
    Text,
    Link,
    useBreakpointValue,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    AuthHelpers,
    useAccessToken,
    useAuthHelper,
    useCustomerId,
    useCustomerType,
    useProducts,
    useShippingMethodsForShipment,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'

import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {Link as RouteLink} from 'react-router-dom'

import Search from '../search-v2'

import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {HideSeachOnMobile, HideSeachOnDesktop} from '../responsive'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {STORE_LOCATOR_IS_ENABLED} from '@salesforce/retail-react-app/app/constants'
import {HeaderNavLayoutProvider} from './header-nav-layout-context'
import {
    headerLogoStyle,
    menuIconStyle,
    toggleMenuIconStyle,
    cartHeadingStyle,
    cartBodyStyle,
    cartFooterStyles,
    cartBadgeStyle,
    priceContentStyle,
    priceDiscountContentStyle,
    giftName
} from './index.style'
import {pxToRem} from '../../utills/pxToRem'
import {transformBasketData, transformGiftCertificateItems} from '../../utills/transformBasket'
import useSitePreference from '../../hooks/use-site-preference'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import '@adyen/adyen-salesforce-pwa/dist/app/adyen.css'
import GooglePayExpress from '../../pages/checkout/components/express-checkout/googlePay'
import ApplePayExpress from '../../pages/checkout/components/express-checkout/applePay'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import AdyenExpressCheckoutProvider from '../../pages/checkout/adyenOverrides/contexts/adyen-express-checkout-context'
import {isEmpty} from '../../utills/objectUtils'

const ASSET_PREFIX = 'static/img/header-v2/'

const SearchBar = (props) => {
    const styles = useMultiStyleConfig('HeaderV2')
    const intl = useIntl()
    const placeholder = intl.formatMessage({
        id: 'header.field.placeholder.search_for_products',
        defaultMessage: 'Search...'
    })
    return (
        <Box {...styles.searchContainer}>
            <Search aria-label={placeholder} placeholder={placeholder} {...props} />
        </Box>
    )
}

const HeaderV2 = ({
    children,
    onMenuClick = noop,
    onMyAccountClick = noop,
    onLogoClick = noop,
    onMyCartClick = noop,
    onWishlistClick = noop,
    onStoreLocatorClick = noop,
    menuToggle,
    setMenuToggle,
    ...props
}) => {
    const {app: appConfig} = getConfig()
    const intl = useIntl()
    const popoverTriggerRef = useRef(null)
    const menuRef = useRef()
    const {
        derivedData: {totalItems},
        data: basket
    } = useCurrentBasket()
    const customerId = useCustomerId()
    const {getTokenWhenReady} = useAccessToken()
    const {locale, site} = useMultiSite()
    const productItemsQuantity =
        basket?.productItems?.reduce((sum, item) => sum + item.quantity, 0) || 0
    const giftCertificateItemsCount = basket?.giftCertificateItems?.length || 0
    const totalItemsWithGiftCards = productItemsQuantity + giftCertificateItemsCount

    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true,
                c_storepickup: true,
                c_isPDPRequest: true,
                locale: 'default'
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const [miniCartItems, setMiniCartItems] = useState({})
    const {isRegistered} = useCustomerType()
    const {data: customer} = useCurrentCustomer()
    const logout = useAuthHelper(AuthHelpers.Logout)

    const onAccountSignoutClick = async () => {
        onAccountDropdownClose()
        await logout.mutateAsync()
        window.location.href = '/login'
    }
    const navigate = useNavigation()
    const {
        getButtonProps: getAccountMenuButtonProps,
        getDisclosureProps: getAccountMenuDisclosureProps,
        isOpen: isAccountMenuOpen,
        onClose: onAccountMenuClose,
        onOpen: onAccountMenuOpen
    } = useDisclosure()
    const {
        getButtonProps: getAccountDropdownButtonProps,
        isOpen: isAccountDropdownOpen,
        onClose: onAccountDropdownClose,
        onOpen: onAccountDropdownOpen
    } = useDisclosure()

    const hasEnteredAccountDropdownContent = useRef(false)
    const accountDropdownCloseTimeoutRef = useRef(null)

    const [showLoading, setShowLoading] = useState(false)
    const [isAboveHeaderVisible, setIsAboveHeaderVisible] = useState(true)
    const [isHeaderScrolled, setIsHeaderScrolled] = useState(false)
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false)
    // tracking if users enter the popover Content,
    // so we can decide whether to close the menu when users leave account icons
    const hasEnterPopoverContent = useRef()
    const isMobile = useBreakpointValue({base: true, md: false})
    const isTablet = useBreakpointValue({base: false, sm: false, md: true, lg: false})
    const isDesktop = !isMobile
    const headerFlexRef = useRef(null)
    const logoRef = useRef(null)
    const searchRef = useRef(null)
    const navContentRef = useRef(null)

    const styles = useMultiStyleConfig('HeaderV2')
    const [authToken, setAuthToken] = useState()
    const {data} = useSitePreference('pdpVariationCategories')
    const pdpVariationCategory = data?.pdpVariationCategories
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )

    const {data: shippingMethods} = useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me',
                c_isCart: true
            }
        },
        {
            enabled:
                !!basket?.basketId &&
                basket.shipments.length > 0 &&
                (basket?.productItems?.length > 0 || basket?.giftCertificateItems?.length > 0) &&
                !basket.shipments[0].shippingMethod
        }
    )

    useEffect(() => {
        if (basket && Object.keys(basket).length > 0) {
            let basketItems = []
            let productSubTotal = 0
            // Handle regular product items
            if (products && Object.keys(products).length > 0 && basket.productItems) {
                const transformedProductData = transformBasketData(products, basket)
                basketItems = [...basketItems, ...transformedProductData]
                productSubTotal += parseFloat(basket.productSubTotal || 0)
            }
            // Handle gift certificate items
            if (basket.giftCertificateItems && basket.giftCertificateItems.length > 0) {
                const transformedGiftCertificates = transformGiftCertificateItems(
                    basket.giftCertificateItems
                )
                basketItems = [...basketItems, ...transformedGiftCertificates]
                // Add gift certificate amounts to subtotal
                const giftCertificateTotal = basket.giftCertificateItems.reduce(
                    (total, item) => total + parseFloat(item.amount || 0),
                    0
                )
                productSubTotal += giftCertificateTotal
            }
            const cartItems = {
                basketItems: basketItems,
                productSubTotal: productSubTotal.toFixed(2)
            }
            setMiniCartItems(cartItems)
        }
    }, [products, basket])

    useEffect(() => {
        const handleScroll = () => {
            const aboveHeaderEl = document.querySelector('.above-header-wrapper')
            const aboveHeaderHeight = aboveHeaderEl?.offsetHeight || 0
            setIsAboveHeaderVisible(aboveHeaderHeight === 0 || window.scrollY < aboveHeaderHeight)
            setIsHeaderScrolled(window.scrollY > 0)
        }

        window.addEventListener('scroll', handleScroll)
        handleScroll()

        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    useEffect(() => {
        if (!appConfig.digioh.enabled) return
        const userAgent = navigator.userAgent

        // skip specific bots or tools
        const blocked = /house|Speed|GTmetrix|moto g power/i.test(userAgent)
        if (blocked || !appConfig.digioh || !appConfig.digioh.scriptUrl) return

        // dynamically load the external script
        const script = document.createElement('script')
        script.src = `${window.location.protocol === 'https:' ? 'https://' : 'http://'}${
            appConfig.digioh.scriptUrl
        }?mb=${new Date().getTime()}`
        script.async = true
        document.body.appendChild(script)

        return () => {
            // cleanup if component unmounts
            document.body.removeChild(script)
        }
    }, [])

    useEffect(() => {
        if (
            basket?.basketId &&
            basket?.shipments?.length > 0 &&
            !basket?.shipments[0].shippingMethod &&
            shippingMethods
        ) {
            updateShippingMethodForShipmentsMutation.mutate({
                parameters: {
                    basketId: basket?.basketId,
                    shipmentId: 'me'
                },
                body: {
                    id: shippingMethods.defaultShippingMethodId
                }
            })
        }
    }, [basket?.shipments, shippingMethods])

    useEffect(() => {
        const getToken = async () => {
            const token = await getTokenWhenReady()
            setAuthToken(token)
        }

        getToken()
    }, [])

    const handleOnMenuClick = () => {
        setMenuToggle(!menuToggle)
        onMenuClick()
    }

    const handleIconsMouseLeave = () => {
        // don't close the menu if users enter the popover content
        setTimeout(() => {
            if (!hasEnterPopoverContent.current) onAccountMenuClose()
        }, 100)
    }

    const handleKeyDown = (event) => {
        if (event.key === 'Tab' && event.shiftKey && isAccountMenuOpen) {
            // Prevent default behavior to keep focus on the popup trigger
            event.preventDefault()
            popoverTriggerRef.current.focus()
        }
    }

    const isMixedCart =
        basket?.shipments?.length > 1 &&
        basket.shipments.map((s) => s.c_fromStoreId).filter((v) => v !== undefined)?.length !==
            basket?.shipments?.length

    // Show horizontal nav on desktop (lg), hamburger on mobile/tablet (base/sm/md)
    const showHorizontalNav = isDesktop && !isTablet
    const showHamburger = !showHorizontalNav
    const singleRowGap = pxToRem(20)
    if (!authToken) {
        return
    }

    return (
        <HeaderNavLayoutProvider value={{isTwoRow: false}}>
            <Box {...styles.container} {...props} id="sticky-header">
                <Box {...styles.content}>
                    {showLoading && <LoadingSpinner wrapperStyles={{height: '100vh'}} />}

                    <Box
                        display={{base: 'block', sm: 'block', md: 'flex', lg: 'flex'}}
                        flexDirection="column"
                        height={{base: 'auto', sm: 'auto', md: '100%', lg: '100%'}}
                        position="relative"
                    >
                        <Flex
                            ref={headerFlexRef}
                            flex={{base: '0 0 auto', md: 1, lg: 1}}
                            minHeight={{base: 0, md: 0, lg: 0}}
                            alignItems="center"
                            justifyContent="space-between"
                            flexWrap="nowrap"
                            gap={{base: 0, md: singleRowGap, lg: singleRowGap}}
                            paddingLeft={{base: 0, sm: 0, md: 0, lg: 0}}
                            paddingRight={{base: 0, sm: 0, md: 0, lg: 0}}
                            paddingTop={{base: pxToRem(15), md: 0, lg: 0}}
                            paddingBottom={{base: pxToRem(15), md: 0, lg: 0}}
                            marginBottom={0}
                        >
                            <Flex
                                ref={logoRef}
                                alignItems="center"
                                gap={{base: pxToRem(15), md: pxToRem(25), lg: pxToRem(30)}}
                                minWidth={{base: 0, sm: 0, md: 'auto', lg: 'auto'}}
                                flexShrink={{base: 1, sm: 1, md: 0, lg: 0}}
                            >
                                {/* menu icon when horizontal nav is hidden (mobile, tablet, or below 1400px) */}
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    as="button"
                                    type="button"
                                    aria-label={intl.formatMessage({
                                        id: 'header.button.assistive_msg.menu',
                                        defaultMessage: 'Menu'
                                    })}
                                    title={intl.formatMessage({
                                        id: 'header.button.assistive_msg.menu.open_dialog',
                                        defaultMessage: 'Opens a dialog'
                                    })}
                                    ref={menuRef}
                                    onClick={handleOnMenuClick}
                                    backgroundColor="transparent"
                                    border="none"
                                    cursor="pointer"
                                    padding={0}
                                    color="white"
                                    sx={{
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        '@media (min-width: 1400px)': {
                                            display: 'none'
                                        }
                                    }}
                                >
                                    <IconButton
                                        aria-hidden
                                        pointerEvents="none"
                                        sx={menuToggle ? toggleMenuIconStyle : menuIconStyle}
                                        icon={<i className="menu-icon fa fa-bars"></i>}
                                        variant="unstyled"
                                        {...styles.icons}
                                        position="relative"
                                        minWidth={0}
                                        width={{
                                            base: pxToRem(23),
                                            sm: pxToRem(23),
                                            md: pxToRem(28),
                                            lg: pxToRem(28)
                                        }}
                                        height={{
                                            base: pxToRem(17),
                                            sm: pxToRem(17),
                                            md: pxToRem(20),
                                            lg: pxToRem(20)
                                        }}
                                    />
                                </Box>

                                {/* header logo */}
                                <IconButton
                                    as="a"
                                    href="/"
                                    aria-label={intl.formatMessage({
                                        id: 'header.button.assistive_msg.logo',
                                        defaultMessage: "Cavender's"
                                    })}
                                    sx={headerLogoStyle}
                                    icon={
                                        <img
                                            src={getAssetUrl(`${ASSET_PREFIX}header-logo.svg`)}
                                            height={{base: 30, md: 34}}
                                            width={{base: 128.5, md: 145.59}}
                                            alt="Cavender's"
                                        />
                                    }
                                    {...styles.logoIcon}
                                    variant="unstyled"
                                    onClick={onLogoClick}
                                />
                            </Flex>

                            {/* Horizontal nav container - shows at 1400px and above with MegaMenu inside */}
                            <Box
                                {...styles.bodyContainer}
                                ref={navContentRef}
                                display={{base: 'none', md: 'none', lg: 'none', xl: 'none'}}
                                sx={{
                                    '@media (min-width: 1400px)': {
                                        display: 'flex'
                                    }
                                }}
                                flex="1 1 auto"
                                alignSelf="stretch"
                                alignItems="stretch"
                                justifyContent="center"
                                minWidth={0}
                                marginLeft={0}
                                marginRight={0}
                                width="100%"
                                height="100%"
                                minHeight={0}
                                flexDirection="column"
                                overflow="visible"
                                position="relative"
                            >
                                {children}
                            </Box>

                            <Flex
                                ref={searchRef}
                                flex={{base: '0 0 auto', md: '0 0 auto'}}
                                alignItems="center"
                                justifyContent="flex-end"
                                columnGap={{
                                    base: pxToRem(10),
                                    sm: pxToRem(10),
                                    md: 'clamp(0.25rem, 0.5vw, 0.5rem)',
                                    lg: 'clamp(0.25rem, 0.5vw, 0.5rem)'
                                }}
                                minWidth={0}
                                flexShrink={0}
                                alignSelf={{md: 'stretch', lg: 'stretch'}}
                            >
                                <Box
                                    as="button"
                                    type="button"
                                    display={{base: 'inline-flex', sm: 'inline-flex', md: 'none'}}
                                    flex="0 0 auto"
                                    alignItems="center"
                                    justifyContent="center"
                                    color="white"
                                    _hover={{color: 'white', opacity: 0.9}}
                                    backgroundColor="transparent"
                                    border="none"
                                    cursor="pointer"
                                    padding={0}
                                    overflow="hidden"
                                    aria-label={intl.formatMessage({
                                        id: 'header.button.assistive_msg.search',
                                        defaultMessage: 'Search'
                                    })}
                                    onClick={() => setIsMobileSearchOpen((prev) => !prev)}
                                >
                                    <img
                                        src={getAssetUrl(`${ASSET_PREFIX}search-icon.svg`)}
                                        alt=""
                                        width={{base: 30, md: 20}}
                                        height={{base: 30, md: 20}}
                                        style={{display: 'block', marginRight: '3px'}}
                                    />
                                </Box>
                                <HideSeachOnMobile>
                                    <Box flex="0 0 auto" minWidth={0}>
                                        <SearchBar />
                                    </Box>
                                </HideSeachOnMobile>

                                {STORE_LOCATOR_IS_ENABLED && (
                                    <Box flex="0 0 auto" sx={styles.iconWrapper}>
                                        <Link
                                            as="a"
                                            href="/storelocator"
                                            aria-label={intl.formatMessage({
                                                defaultMessage: 'Store Locator',
                                                id: 'header.button.assistive_msg.store_locator'
                                            })}
                                            color="white"
                                            textDecoration="none"
                                            sx={{...styles.iconInner, ...styles.storeLocatorIcon}}
                                        >
                                            <img
                                                src={getAssetUrl(
                                                    `${ASSET_PREFIX}header-location-icon.svg`
                                                )}
                                                alt=""
                                                width={{base: 24, md: 20, lg: 22}}
                                                height={{base: 30, md: 20, lg: 27}}
                                                style={{display: 'block'}}
                                            />
                                        </Link>
                                    </Box>
                                )}

                                {/* Account — click icon opens account popover (X or click outside to close) */}
                                <Box
                                    flex="0 0 auto"
                                    marginLeft={
                                        STORE_LOCATOR_IS_ENABLED
                                            ? {
                                                  md: 'clamp(0.25rem, 0.5vw, 0.5rem)',
                                                  lg: 'clamp(0.25rem, 0.5vw, 0.5rem)'
                                              }
                                            : 0
                                    }
                                    sx={styles.iconWrapper}
                                >
                                    <Popover
                                        isOpen={isAccountDropdownOpen}
                                        onClose={onAccountDropdownClose}
                                        placement="bottom-end"
                                        isLazy
                                    >
                                        <PopoverTrigger>
                                            <Box
                                                as="button"
                                                type="button"
                                                aria-label={intl.formatMessage({
                                                    id: 'header.button.assistive_msg.my_account',
                                                    defaultMessage: 'Account'
                                                })}
                                                color="white"
                                                backgroundColor="transparent"
                                                border="none"
                                                cursor="pointer"
                                                fontFamily="inherit"
                                                padding={0}
                                                {...getAccountDropdownButtonProps()}
                                                onClick={(e) => {
                                                    if (!isRegistered) {
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                        onMyAccountClick()
                                                        return
                                                    }
                                                    if (isMobile) {
                                                        if (isAccountDropdownOpen) {
                                                            onAccountDropdownClose()
                                                        } else {
                                                            onAccountDropdownOpen()
                                                        }
                                                        e.preventDefault()
                                                        e.stopPropagation()
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === ' ' || e.key === 'Enter') {
                                                        e.preventDefault()
                                                        if (!isRegistered) {
                                                            onMyAccountClick()
                                                            return
                                                        }
                                                        if (isAccountDropdownOpen) {
                                                            onAccountDropdownClose()
                                                        } else {
                                                            onAccountDropdownOpen()
                                                        }
                                                    }
                                                }}
                                                onMouseEnter={() => {
                                                    if (!isRegistered) {
                                                        return
                                                    }
                                                    if (accountDropdownCloseTimeoutRef.current) {
                                                        clearTimeout(
                                                            accountDropdownCloseTimeoutRef.current
                                                        )
                                                        accountDropdownCloseTimeoutRef.current =
                                                            null
                                                    }
                                                    onAccountDropdownOpen()
                                                }}
                                                onMouseLeave={() => {
                                                    if (!isRegistered) {
                                                        return
                                                    }
                                                    accountDropdownCloseTimeoutRef.current =
                                                        setTimeout(() => {
                                                            if (
                                                                !hasEnteredAccountDropdownContent.current
                                                            ) {
                                                                onAccountDropdownClose()
                                                            }
                                                            accountDropdownCloseTimeoutRef.current =
                                                                null
                                                        }, 150)
                                                }}
                                                sx={{
                                                    ...styles.iconInner,
                                                    ...styles.accountIcon,
                                                    _hover: styles.iconInner._hover,
                                                    _focusWithin: isAccountDropdownOpen
                                                        ? styles.iconInner._focusWithin
                                                        : {
                                                              borderBottomColor: 'transparent',
                                                              backgroundColor: 'transparent'
                                                          }
                                                }}
                                            >
                                                <img
                                                    src={getAssetUrl(
                                                        `${ASSET_PREFIX}header-account-icon.svg`
                                                    )}
                                                    alt=""
                                                    width={{base: 32, md: 22, lg: 32}}
                                                    height={{base: 30, md: 20, lg: 30}}
                                                    style={{display: 'block', flexShrink: 0}}
                                                    aria-hidden
                                                />
                                            </Box>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            sx={{
                                                width: {
                                                    base: 'auto',
                                                    md: 'fit-content'
                                                },
                                                minWidth: {
                                                    base: pxToRem(150),
                                                    md: 'auto'
                                                },
                                                maxWidth: {
                                                    base: pxToRem(200),
                                                    md: 'none'
                                                },
                                                borderRadius: 0,
                                                border: '2px solid black',
                                                marginTop: {base: pxToRem(9), md: pxToRem(-6)},
                                                position: 'fixed',
                                                right: {base: pxToRem(11), md: pxToRem(-20)},
                                                left: {base: 'auto', md: 'auto'},
                                                marginLeft: 0
                                            }}
                                            onMouseEnter={() => {
                                                if (accountDropdownCloseTimeoutRef.current) {
                                                    clearTimeout(
                                                        accountDropdownCloseTimeoutRef.current
                                                    )
                                                    accountDropdownCloseTimeoutRef.current = null
                                                }
                                                hasEnteredAccountDropdownContent.current = true
                                            }}
                                            onMouseLeave={() => {
                                                hasEnteredAccountDropdownContent.current = false
                                                onAccountDropdownClose()
                                            }}
                                        >
                                            <PopoverBody
                                                p={0}
                                                sx={{
                                                    padding: pxToRem(15),
                                                    paddingTop: pxToRem(15),
                                                    paddingLeft: '2rem',
                                                    paddingRight: '2rem',
                                                    display: 'flex',
                                                    flexDirection: isRegistered ? 'column' : 'row',
                                                    justifyContent: isRegistered
                                                        ? 'flex-start'
                                                        : 'space-between',
                                                    alignItems: 'stretch',
                                                    gap: pxToRem(8)
                                                }}
                                            >
                                                {isRegistered ? (
                                                    <>
                                                        <Box
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            <Link
                                                                as={RouteLink}
                                                                to="/account"
                                                                color="black"
                                                                textDecoration="none"
                                                                fontSize={pxToRem(14)}
                                                                fontWeight="bold"
                                                                _hover={{
                                                                    textDecoration: 'underline'
                                                                }}
                                                                onClick={onAccountDropdownClose}
                                                            >
                                                                {`${customer?.firstName || ''} ${
                                                                    customer?.lastName || ''
                                                                }`.trim() ||
                                                                    intl.formatMessage({
                                                                        id: 'header.button.account',
                                                                        defaultMessage: 'Account'
                                                                    })}
                                                            </Link>
                                                            <Box
                                                                as="button"
                                                                type="button"
                                                                aria-label={intl.formatMessage({
                                                                    id: 'header.button.close',
                                                                    defaultMessage: 'Close'
                                                                })}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    padding: 0,
                                                                    fontSize: pxToRem(18),
                                                                    lineHeight: 1
                                                                }}
                                                                onClick={onAccountDropdownClose}
                                                            >
                                                                ×
                                                            </Box>
                                                        </Box>
                                                        <Link
                                                            as={RouteLink}
                                                            to="/account/info"
                                                            display="block"
                                                            py={pxToRem(8)}
                                                            color="black"
                                                            textDecoration="none"
                                                            fontSize={pxToRem(14)}
                                                            _hover={{textDecoration: 'underline'}}
                                                            onClick={onAccountDropdownClose}
                                                        >
                                                            {intl.formatMessage({
                                                                id: 'header.link.account_settings',
                                                                defaultMessage: 'Account Settings'
                                                            })}
                                                        </Link>
                                                        <Link
                                                            as={RouteLink}
                                                            to="/account/orders"
                                                            display="block"
                                                            py={pxToRem(4)}
                                                            color="black"
                                                            textDecoration="none"
                                                            fontSize={pxToRem(14)}
                                                            _hover={{textDecoration: 'underline'}}
                                                            onClick={onAccountDropdownClose}
                                                        >
                                                            {intl.formatMessage({
                                                                id: 'header.link.order_history',
                                                                defaultMessage: 'Order History'
                                                            })}
                                                        </Link>
                                                        <Link
                                                            as={RouteLink}
                                                            to="/account/wishlist"
                                                            display="block"
                                                            py={pxToRem(4)}
                                                            color="black"
                                                            textDecoration="none"
                                                            fontSize={pxToRem(14)}
                                                            _hover={{textDecoration: 'underline'}}
                                                            onClick={onAccountDropdownClose}
                                                        >
                                                            {intl.formatMessage({
                                                                id: 'header.link.wishlist',
                                                                defaultMessage: 'Wish List'
                                                            })}
                                                        </Link>
                                                        <Box
                                                            as="button"
                                                            type="button"
                                                            width="100%"
                                                            mt={2}
                                                            py={pxToRem(10)}
                                                            backgroundColor="cav_hover_brightred"
                                                            color="white"
                                                            border="none"
                                                            cursor="pointer"
                                                            fontFamily="inherit"
                                                            fontSize={pxToRem(14)}
                                                            fontWeight="bold"
                                                            _hover={{
                                                                backgroundColor: 'cav_dark_red'
                                                            }}
                                                            onClick={onAccountSignoutClick}
                                                        >
                                                            {intl.formatMessage({
                                                                id: 'header.link.logout',
                                                                defaultMessage: 'LOGOUT'
                                                            })}
                                                        </Box>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Link
                                                            as="button"
                                                            flex={1}
                                                            textAlign="left"
                                                            py={0}
                                                            px={0}
                                                            color="black"
                                                            textDecoration="none"
                                                            backgroundColor="transparent"
                                                            border="none"
                                                            cursor="pointer"
                                                            fontFamily="inherit"
                                                            fontSize={pxToRem(14)}
                                                            _hover={{
                                                                backgroundColor: 'transparent'
                                                            }}
                                                            onClick={() => {
                                                                onAccountDropdownClose()
                                                                onMyAccountClick()
                                                            }}
                                                        >
                                                            {intl.formatMessage({
                                                                id: 'header.link.login_register',
                                                                defaultMessage: 'Sign in/Sign up'
                                                            })}
                                                        </Link>
                                                        <Box
                                                            as="button"
                                                            type="button"
                                                            aria-label={intl.formatMessage({
                                                                id: 'header.button.close',
                                                                defaultMessage: 'Close'
                                                            })}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                background: 'none',
                                                                border: 'none',
                                                                padding: 0,
                                                                fontSize: pxToRem(18),
                                                                lineHeight: 1,
                                                                flexShrink: 0
                                                            }}
                                                            onClick={onAccountDropdownClose}
                                                        >
                                                            ×
                                                        </Box>
                                                    </>
                                                )}
                                            </PopoverBody>
                                        </PopoverContent>
                                    </Popover>
                                </Box>

                                <Box flex="0 0 auto" sx={styles.iconWrapper}>
                                    <Popover
                                        isLazy
                                        arrowSize={15}
                                        gutter={0}
                                        isOpen={
                                            miniCartItems?.basketItems?.length > 0 &&
                                            isAccountMenuOpen &&
                                            !isMobile
                                        }
                                        placement="bottom-end"
                                        onClose={onAccountMenuClose}
                                        onOpen={onAccountMenuOpen}
                                    >
                                        <PopoverTrigger>
                                            <Box
                                                as="a"
                                                href="/cart"
                                                title={
                                                    isMobile
                                                        ? intl.formatMessage({
                                                              id: 'header.button.view_cart',
                                                              defaultMessage: 'View Cart'
                                                          })
                                                        : undefined
                                                }
                                                aria-label={intl.formatMessage(
                                                    {
                                                        id: 'checkout_header.mini_cart.cart_with_count',
                                                        defaultMessage:
                                                            'Cart, {itemCount, plural, =0 {no items} one {# item} other {# items}}'
                                                    },
                                                    {itemCount: totalItemsWithGiftCards || 0}
                                                )}
                                                color="white"
                                                textDecoration="none"
                                                backgroundColor="transparent"
                                                border="none"
                                                cursor="pointer"
                                                fontFamily="inherit"
                                                padding={0}
                                                position="relative"
                                                display="inline-flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                onClick={(e) => {
                                                    if (
                                                        !isMobile &&
                                                        miniCartItems?.basketItems?.length > 0
                                                    ) {
                                                        e.preventDefault()
                                                        getAccountMenuButtonProps().onClick?.()
                                                    }
                                                }}
                                                onMouseOver={onAccountMenuOpen}
                                                onMouseLeave={handleIconsMouseLeave}
                                                onPointerEnter={onAccountMenuOpen}
                                                onPointerLeave={handleIconsMouseLeave}
                                                ref={popoverTriggerRef}
                                                onKeyDown={handleKeyDown}
                                                {...getAccountMenuButtonProps()}
                                                sx={{
                                                    ...styles.iconInner,
                                                    ...styles.accountIcon,
                                                    borderRadius: 0,
                                                    _hover: {
                                                        ...(styles.iconInner._hover || {}),
                                                        borderRadius: 0
                                                    }
                                                }}
                                            >
                                                <img
                                                    src={getAssetUrl(
                                                        `${ASSET_PREFIX}header-cart-icon.svg`
                                                    )}
                                                    alt={intl.formatMessage({
                                                        id: 'checkout_header.mini_cart.cart',
                                                        defaultMessage: 'Cart'
                                                    })}
                                                    width={{base: 41, md: 27, lg: 41}}
                                                    height={{base: 30, md: 20, lg: 30}}
                                                    style={{display: 'block', flexShrink: 0}}
                                                    aria-hidden
                                                />
                                                {basket && totalItemsWithGiftCards > 0 && (
                                                    <Badge
                                                        variant="notification"
                                                        sx={{
                                                            ...cartBadgeStyle,
                                                            backgroundColor: isMobile
                                                                ? 'cav_hover_brightred'
                                                                : isAboveHeaderVisible
                                                                ? 'cav_hover_brightred'
                                                                : 'cav_btn_light_black',
                                                            borderColor: isAboveHeaderVisible
                                                                ? 'cav_hover_brightred'
                                                                : 'cav_btn_light_black',
                                                            _hover: {
                                                                backgroundColor:
                                                                    isAboveHeaderVisible
                                                                        ? 'cav_dark_red'
                                                                        : 'cav_hover_brightred',
                                                                borderColor: isAboveHeaderVisible
                                                                    ? 'cav_dark_red'
                                                                    : 'cav_hover_brightred'
                                                            }
                                                        }}
                                                    >
                                                        {totalItemsWithGiftCards}
                                                    </Badge>
                                                )}
                                            </Box>
                                        </PopoverTrigger>

                                        <PopoverContent
                                            {...styles.popoverContent}
                                            onMouseLeave={() => {
                                                hasEnterPopoverContent.current = false
                                                onAccountMenuClose()
                                            }}
                                            onMouseOver={() => {
                                                hasEnterPopoverContent.current = true
                                            }}
                                            {...getAccountMenuDisclosureProps()}
                                            sx={{
                                                width: pxToRem(334),
                                                borderRadius: '0',
                                                border: '2px solid black',

                                                position: 'fixed',
                                                right: pxToRem(0),
                                                left: 'auto',
                                                marginRight: 0,

                                                marginTop: pxToRem(-14),

                                                '& [data-popper-arrow], & .chakra-popover__arrow-positioner':
                                                    {marginLeft: pxToRem(25)}
                                            }}
                                        >
                                            <PopoverArrow />
                                            <PopoverHeader
                                                sx={{
                                                    padding: '0',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    ...cartHeadingStyle
                                                }}
                                            >
                                                <Box as="h5">
                                                    {intl.formatMessage({
                                                        defaultMessage: 'ITEMS IN YOUR CART'
                                                    })}{' '}
                                                    ({totalItemsWithGiftCards})
                                                </Box>
                                                <Box
                                                    sx={{cursor: 'pointer'}}
                                                    onClick={onAccountMenuClose}
                                                >
                                                    X
                                                </Box>
                                            </PopoverHeader>
                                            <PopoverBody sx={cartBodyStyle}>
                                                <Box as="nav">
                                                    <Box data-testid="account-detail-nav">
                                                        {miniCartItems?.basketItems?.map((item) => {
                                                            const {
                                                                id,
                                                                name,
                                                                price,
                                                                priceAfterItemDiscount,
                                                                quantity,
                                                                imageUrl,
                                                                priceData,
                                                                tieredPrices,
                                                                productUrl,
                                                                variationValues,
                                                                isGiftCertificate,
                                                                recipientName,
                                                                recipientEmail,
                                                                senderName,
                                                                message,
                                                                currency,
                                                                isPhysicalGiftCard
                                                            } = item
                                                            if (isGiftCertificate) {
                                                                return (
                                                                    <Flex key={id} gap={2}>
                                                                        <Box flex="0 0 29%">
                                                                            <Link href={productUrl}>
                                                                                <img
                                                                                    src={imageUrl}
                                                                                    alt={name}
                                                                                />
                                                                            </Link>
                                                                        </Box>
                                                                        <Box
                                                                            flex="1"
                                                                            sx={priceContentStyle}
                                                                        >
                                                                            <Text
                                                                                className="property-container"
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    flexDirection:
                                                                                        'column',
                                                                                    gap: 1
                                                                                }}
                                                                            >
                                                                                <Text
                                                                                    as="span"
                                                                                    className="property-item"
                                                                                    sx={giftName}
                                                                                >
                                                                                    {name}
                                                                                </Text>
                                                                                <span className="property-item">
                                                                                    Emailed to:{' '}
                                                                                    {recipientName}
                                                                                </span>
                                                                                {recipientEmail && (
                                                                                    <span className="property-item fullWidth">
                                                                                        {
                                                                                            recipientEmail
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                                {senderName && (
                                                                                    <span className="property-item fullWidth">
                                                                                        From:{' '}
                                                                                        {senderName}
                                                                                    </span>
                                                                                )}
                                                                                <Text
                                                                                    as="span"
                                                                                    sx={{
                                                                                        fontWeight:
                                                                                            'bold'
                                                                                    }}
                                                                                >
                                                                                    Price:{' '}
                                                                                    <FormattedNumber
                                                                                        value={
                                                                                            price
                                                                                        }
                                                                                        style="currency"
                                                                                        currency={
                                                                                            basket?.currency
                                                                                        }
                                                                                    />
                                                                                </Text>
                                                                                <Text as="span">
                                                                                    Delivery Method:
                                                                                    Email Delivery
                                                                                </Text>
                                                                            </Text>
                                                                        </Box>
                                                                    </Flex>
                                                                )
                                                            } else {
                                                                const productAttributes = {}
                                                                const needChange =
                                                                    pdpVariationCategory?.includes(
                                                                        item?.primaryCategoryId ||
                                                                            item?.c_primaryCatergoryID
                                                                    )
                                                                if (variationValues?.waist)
                                                                    productAttributes['Waist'] =
                                                                        variationValues?.waist

                                                                if (variationValues?.size) {
                                                                    if (needChange) {
                                                                        productAttributes['Waist'] =
                                                                            variationValues?.size
                                                                    } else {
                                                                        productAttributes['Size'] =
                                                                            variationValues?.size
                                                                    }
                                                                }

                                                                if (variationValues?.width) {
                                                                    if (needChange) {
                                                                        productAttributes[
                                                                            'Length'
                                                                        ] = variationValues?.width
                                                                    } else {
                                                                        productAttributes['Width'] =
                                                                            variationValues?.width
                                                                    }
                                                                }

                                                                if (variationValues?.length)
                                                                    productAttributes['Length'] =
                                                                        variationValues?.length
                                                                if (quantity)
                                                                    productAttributes['Qty'] =
                                                                        quantity

                                                                const hasWidth =
                                                                    variationValues?.width

                                                                return (
                                                                    <Flex key={id} gap={2}>
                                                                        <Box flex="0 0 29%">
                                                                            <Link href={productUrl}>
                                                                                <img
                                                                                    src={imageUrl}
                                                                                    alt={name}
                                                                                />
                                                                            </Link>
                                                                        </Box>
                                                                        <Box
                                                                            flex="1"
                                                                            sx={priceContentStyle}
                                                                        >
                                                                            <Link href={productUrl}>
                                                                                {name}
                                                                            </Link>
                                                                            <Text className="property-container">
                                                                                {Object.entries(
                                                                                    productAttributes
                                                                                ).map(
                                                                                    (
                                                                                        [
                                                                                            key,
                                                                                            value
                                                                                        ],
                                                                                        index
                                                                                    ) => (
                                                                                        <span
                                                                                            key={
                                                                                                index
                                                                                            }
                                                                                            className={
                                                                                                hasWidth &&
                                                                                                key ===
                                                                                                    'Qty'
                                                                                                    ? 'qty-new-line'
                                                                                                    : ''
                                                                                            }
                                                                                        >
                                                                                            {index %
                                                                                                2 ===
                                                                                                1 &&
                                                                                                !(
                                                                                                    hasWidth &&
                                                                                                    key ===
                                                                                                        'Qty'
                                                                                                ) && (
                                                                                                    <span className="pipe">
                                                                                                        |
                                                                                                    </span>
                                                                                                )}
                                                                                            {key ===
                                                                                                'Size' &&
                                                                                            isPhysicalGiftCard ? (
                                                                                                <>
                                                                                                    {
                                                                                                        key
                                                                                                    }

                                                                                                    :{' '}
                                                                                                    <FormattedNumber
                                                                                                        value={
                                                                                                            value
                                                                                                        }
                                                                                                        style="currency"
                                                                                                        currency={
                                                                                                            currency
                                                                                                        }
                                                                                                    />
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    {
                                                                                                        key
                                                                                                    }

                                                                                                    :{' '}
                                                                                                    {key ===
                                                                                                        'Size' ||
                                                                                                    key ===
                                                                                                        'Waist'
                                                                                                        ? item?.c_selectedVariationSize ||
                                                                                                          value
                                                                                                        : value}
                                                                                                </>
                                                                                            )}
                                                                                        </span>
                                                                                    )
                                                                                )}
                                                                            </Text>
                                                                            {priceData?.currentPrice !=
                                                                                priceData?.listPrice && (
                                                                                <Text
                                                                                    sx={
                                                                                        priceDiscountContentStyle
                                                                                    }
                                                                                >
                                                                                    <FormattedNumber
                                                                                        value={
                                                                                            priceData?.listPrice
                                                                                        }
                                                                                        style="currency"
                                                                                        currency={
                                                                                            basket?.currency
                                                                                        }
                                                                                    />
                                                                                </Text>
                                                                            )}
                                                                            <Text fontWeight="bold">
                                                                                <FormattedNumber
                                                                                    value={
                                                                                        priceData?.currentPrice
                                                                                    }
                                                                                    style="currency"
                                                                                    currency={
                                                                                        basket?.currency
                                                                                    }
                                                                                />
                                                                            </Text>
                                                                        </Box>
                                                                    </Flex>
                                                                )
                                                            }
                                                        })}
                                                    </Box>
                                                </Box>
                                            </PopoverBody>
                                            <PopoverFooter sx={cartFooterStyles}>
                                                <Box className="mini-cart-subtotals">
                                                    <Text>
                                                        {intl.formatMessage({
                                                            id: 'checkout_header.mini_cart.sub_total',
                                                            defaultMessage: 'Order Subtotal'
                                                        })}
                                                        :{' '}
                                                        <FormattedNumber
                                                            value={miniCartItems?.productSubTotal}
                                                            style="currency"
                                                            currency={basket?.currency}
                                                        />
                                                    </Text>
                                                    <Box
                                                        justifyContent="center"
                                                        className="btn-container"
                                                    >
                                                        <a className="button" href="/cart">
                                                            {intl.formatMessage({
                                                                id: 'checkout_header.mini_cart.view_cart',
                                                                defaultMessage: 'View Cart'
                                                            })}
                                                        </a>
                                                    </Box>
                                                    {basket &&
                                                        basket?.orderTotal > 0 &&
                                                        !isMixedCart && (
                                                            <AdyenExpressCheckoutProvider
                                                                authToken={authToken}
                                                                customerId={customerId}
                                                                locale={locale}
                                                                site={site}
                                                                basket={basket}
                                                                navigate={navigate}
                                                                isMiniCart={true}
                                                            >
                                                                <GooglePayExpress
                                                                    shippingMethods={
                                                                        shippingMethods
                                                                    }
                                                                />
                                                                <ApplePayExpress
                                                                    shippingMethods={
                                                                        shippingMethods
                                                                    }
                                                                />
                                                            </AdyenExpressCheckoutProvider>
                                                        )}
                                                </Box>
                                            </PopoverFooter>
                                        </PopoverContent>
                                    </Popover>
                                </Box>
                            </Flex>
                        </Flex>
                    </Box>

                    {/* search for mobile - below header, toggled by search icon */}
                    <HideSeachOnDesktop>
                        {isMobileSearchOpen && (
                            <SearchBar onClearAndClose={() => setIsMobileSearchOpen(false)} />
                        )}
                    </HideSeachOnDesktop>
                </Box>

                {/* Mega menu dropdown slot: only when horizontal nav is visible */}
                <Box
                    id="mega-menu-dropdown-slot"
                    width="100%"
                    position="absolute"
                    left={0}
                    top="100%"
                    zIndex="dropdown"
                    display={showHorizontalNav ? 'block' : 'none'}
                />
            </Box>
        </HeaderNavLayoutProvider>
    )
}

HeaderV2.propTypes = {
    children: PropTypes.node,
    onMenuClick: PropTypes.func,
    onLogoClick: PropTypes.func,
    onMyAccountClick: PropTypes.func,
    onWishlistClick: PropTypes.func,
    onMyCartClick: PropTypes.func,
    onStoreLocatorClick: PropTypes.func,
    searchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.elementType})
    ]),
    menuToggle: PropTypes.bool,
    setMenuToggle: PropTypes.func
}

export default HeaderV2
